import {Component, effect, ElementRef, inject, OnInit, signal, viewChild} from '@angular/core';
import {TaAppStore} from '../../../state/clcta-store';
import {Card} from 'primeng/card';
import {TableModule} from 'primeng/table';
import {Button} from 'primeng/button';
import {TreeTable, TreeTableModule} from 'primeng/treetable';
import {TreeNode} from 'primeng/api';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ManageUnitModalComponent} from '../../components/admin/manage-unit-modal/manage-unit-modal.component';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';
import {toTAStatusText} from '../../../util/app.types';
import {Tag} from 'primeng/tag';
import {AdminService, BasicUserDetails, ClcUnitDetails, ProjectDetails, UserDetails} from '../../../api';
import {ManageProjectModalComponent} from '../../components/admin/manage-project-modal/manage-project-modal.component';
import {LoadingService} from '../../../services/loading.service';
import {ErrorMessageService} from '../../../services/error-message.service';
import {ScrollHeightDirective} from '../../directives/scroll-height.directive';
import {DatePipe} from '@angular/common';
import {HttpResponse} from '@angular/common/http';

@Component({
  selector: 'clc-units-projects-admin',
  imports: [
    Card,
    TableModule,
    Button,
    TreeTableModule,
    FormsModule,
    InputText,
    Select,
    Tag,
    ScrollHeightDirective,
    DatePipe,
  ],
  templateUrl: './clc-units-projects-admin.html',
  styleUrl: './clc-units-projects-admin.scss',
  providers: [DialogService],
})
export class ClcUnitsProjectsAdmin implements OnInit {
  dialogService = inject(DialogService);
  adminApi = inject(AdminService);
  loadingService = inject(LoadingService);
  errorMessageService = inject(ErrorMessageService);
  store = inject(TaAppStore);
  ref: DynamicDialogRef | null | undefined;

  unitsList: ClcUnitDetails[] = [];
  availableApprovers: UserDetails[] = [];
  tableRows: TreeNode[] = [];

  statusOptions = [
    { label: 'Active', value: true,  severity: 'info' },
    { label: 'Inactive', value: false, severity: 'secondary' }
  ];
  filterInput = signal('');
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;
  private tt = viewChild.required<TreeTable>('tt');
  exporting = signal(false);

  constructor() {
    effect(() => {
      const value = this.filterInput();
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.applyGlobalFilter(value), 200);
    });
  }

  ngOnInit() {
    this.store.updateBreadCrumbData({
      title: 'Units and Projects',
      titleIcon: 'fa-regular fa-diagram-project fa-lg'
    });
    this.loadUnits();
    this.loadAvailableApprovers();
  }

  loadUnits(): void {
    this.loadingService.showLoading({message: 'Loading Units'});

    this.adminApi.getAllUnits().subscribe({
      next: (response) => {
        if (response.success && !!response.data) {
          this.unitsList = response.data.sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''));
          this.tableRows = this.unitsToTreeNodes(this.unitsList);
        } else {
          console.error('Error getting full itinerary list for acquittal approval: ', response.error);
        }
        this.loadingService.hideLoading();
      },
      error: (error) => {
        console.error('removeCurrentItineraryItem called', error);
        this.loadingService.hideLoading();
      }
    });
  }

  exportUnitsAndProjects(): void {
    const xlsxType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    this.exporting.set(true);
    this.adminApi.getUnitsAndProjectsExport('response', false, {httpHeaderAccept: xlsxType as any}).subscribe({
      next: (response: HttpResponse<any>) => {
        const url = window.URL.createObjectURL(new Blob([response.body], {type: xlsxType}));
        const a = document.createElement('a');
        a.href = url;
        const disposition = response.headers.get('Content-Disposition');
        const filenameMatch = disposition?.match(/filename="([^"]+)"|filename=([^;\s]+)/);
        a.download = filenameMatch?.[1] ?? filenameMatch?.[2] ?? 'UnitsAndProjects_Export.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
        this.exporting.set(false);
      },
      error: err => {
        console.error('Units and Projects export failed', err);
        this.errorMessageService.showTemporaryErrorWithTitle('Export Failed', 'The Units and Projects export could not be generated.', 8000);
        this.exporting.set(false);
      },
    });
  }

  loadAvailableApprovers(): void {
    this.adminApi.getAllTaUsers().subscribe({
      next: resp => {
        this.availableApprovers = resp.data as BasicUserDetails[];
      },
      error: error => {
        console.log('Error loading users: ', error);
      }
    })
  }

  /** Clear applies immediately rather than waiting out the debounce. */
  clearGlobalFilter(): void {
    clearTimeout(this.debounceTimer);
    this.filterInput.set('');
    this.applyGlobalFilter('');
  }

  /**
   * Expansion is set in place: a new tableRows array would make TreeTable re-filter on input change,
   * then filter again via filterGlobal. filterGlobal's own pass re-serializes the rows.
   */
  applyGlobalFilter(value: string): void {
    const term = (value ?? '').trim().toLowerCase();
    const matches = (v?: string | null) => !!v && v.toLowerCase().includes(term);

    for (const node of this.tableRows) {
      node.expanded = term.length > 0
        && (node.children ?? []).some(c => matches(c.data?.code) || matches(c.data?.name));
    }

    this.tt().filterGlobal(value, 'contains');
  }

  unitsToTreeNodes(units: ClcUnitDetails[]): TreeNode[] {
    const nodes = units.map((unit, ii) => ({
      key: `unit-${unit.id ?? ii}`,
      data: {
        type: 'unit',
        approverSum: unit.projects?.reduce((sum, value) => sum + (value.approvers?.length ?? 0), 0)?? 0,
        ...unit
      },
      children: (unit.projects ?? []).map(project => ({
        key: `project-${unit.id ?? ii}`,
        data: {
          type: 'project',
          ...project
        },
        leaf: true
      }))
    }));

    this.applyStripes(nodes);
    return nodes;
  }

  /**
   * Stripes alternate at the unit level: each unit takes the next stripe in the
   * sequence and its projects inherit it, so a whole unit group reads as one band.
   * TreeTable's body template exposes no row index, so the flag lives on row data.
   */
  applyStripes(units: TreeNode[]): void {
    units.forEach((unit, ii) => {
      const striped = ii % 2 === 0;
      unit.data.striped = striped;
      (unit.children ?? []).forEach(project => (project.data.striped = striped));
    });
  }

  /**
   * Re-stripe against what is actually visible — filtered-out units would
   * otherwise consume a stripe slot and leave two grey groups adjacent.
   * TreeTable shallow-copies nodes when filtering, so `data` is shared with
   * the originals and mutating it here updates the source rows too.
   */
  onTableFilter(filteredValue: TreeNode[] | undefined): void {
    this.applyStripes(filteredValue ?? []);
  }

  openModal(rowData: any): void {
    if (rowData.type === 'unit') {
      this.openEditUnitModal(rowData as ClcUnitDetails);
    } else if (rowData.type === 'project') {
      this.openEditProjectModal(rowData as ProjectDetails);
    }
  }

  unitModalConfig(title: string, data: any, availableManagers: UserDetails[]): any {
    return {
      header: title,
      inputValues: {
        modalData: data,
        availableManagers: availableManagers,
        availableApprovers: availableManagers
      },
      contentStyle: {
        overflow: 'auto'
      },
      modal: true,
      width: 'fit',
    };
  }

  projectModalConfig(title: string, data: any, availableApprovers: UserDetails[], availableUnits?: ClcUnitDetails[]): any {
    return {
      header: title,
      inputValues: {
        modalData: data,
        availableApprovers: availableApprovers,
        availableUnits: availableUnits
      },
      contentStyle: {
        overflow: 'auto'
      },
      modal: true,
      width: 'fit',
    };
  }

  openEditUnitModal(unitDetails: ClcUnitDetails): void {
    this.ref = this.dialogService.open(ManageUnitModalComponent, this.unitModalConfig("Edit Unit", unitDetails, this.availableApprovers));
    this.ref?.onClose.subscribe((data) => {
      if (!!data) {
        let updatedUnit: ClcUnitDetails = {...unitDetails, ...data, projects: []};
        this.adminApi.updateCreateUnit(updatedUnit).subscribe({
          next: (response) => {
            if (response.success && !!response.data) {
              let index = this.unitsList.findIndex(unit => unit.id === response.data!.id);
              if (index !== -1) {
                this.unitsList.splice(index, 1, {...response.data});
                this.tableRows = this.unitsToTreeNodes(this.unitsList);
              }
            } else {
              console.error('Error getting updating unit', response.error);
              this.errorMessageService.showTemporaryErrorWithTitle('Save Failed', response.error ?? '', 10000);
            }
          },
          error: (error) => {
            console.error('Error getting updating unit', error);
            this.errorMessageService.showTemporaryErrorWithTitle('Save Failed', error.statusText ?? '', 10000);
          }
        });
      }
    });
  }

  openEditProjectModal(projectDetails: ProjectDetails): void {
    this.ref = this.dialogService.open(ManageProjectModalComponent, this.projectModalConfig("Edit Project", projectDetails, this.availableApprovers, this.unitsList));
    this.ref?.onClose.subscribe((data) => {
      if (!!data) {
        let updatedProject: ProjectDetails = {...projectDetails, ...data};

        this.adminApi.updateCreateProject(updatedProject).subscribe({
          next: (response) => {
            if (response.success && !!response.data) {
              if (response.data.unitId !== projectDetails.unitId) {
                this.loadUnits();
              } else {
                let index = this.unitsList.findIndex(unit => unit.id === response.data!.unitId);
                if (index !== -1 && !!this.unitsList.at(index)?.projects) {
                  let projIndex = this.unitsList.at(index)!.projects!.findIndex(project => project.id === response.data!.id);
                  if (projIndex !== -1) {
                    this.unitsList.at(index)!.projects!.splice(projIndex, 1, response.data);
                    this.tableRows = this.unitsToTreeNodes(this.unitsList);
                  }
                }
              }
            } else {
              console.error('Error getting updating project', response.error);
              this.errorMessageService.showTemporaryErrorWithTitle('Save Failed', response.error ?? '', 10000);
            }
          },
          error: (error) => {
            console.error('Error getting updating project', error);
            this.errorMessageService.showTemporaryErrorWithTitle('Save Failed', error.statusText ?? '', 10000);
          }
        });
      }
    });
  }

  openAddUnitModal(): void {
    this.ref = this.dialogService.open(ManageUnitModalComponent, this.unitModalConfig('Add Unit', undefined, this.availableApprovers));
    this.ref?.onClose.subscribe((data) => {
      if (!!data) {
        let newUnit: ClcUnitDetails = {...data};
        this.adminApi.updateCreateUnit(newUnit).subscribe({
          next: (response) => {
            if (response.success && response.data) {
              this.unitsList = [...this.unitsList, response.data].sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''));
              this.tableRows = this.unitsToTreeNodes(this.unitsList);
            } else {
              console.error('Error getting updating unit', response.error);
              this.errorMessageService.showTemporaryErrorWithTitle('Save Failed', response.error ?? '', 10000);
            }
          },
          error: (error) => {
            console.error('Error getting updating unit', error);
            this.errorMessageService.showTemporaryErrorWithTitle('Save Failed', error.statusText ?? '', 10000);
          }
        });
      }
    })
  }

  openAddProjectModal(): void {
    this.ref = this.dialogService.open(ManageProjectModalComponent, this.projectModalConfig('Add Project', undefined, this.availableApprovers, this.unitsList));
    this.ref?.onClose.subscribe((data) => {
      if (!!data) {
        let newProject: ProjectDetails = {...data};
        this.adminApi.updateCreateProject(newProject).subscribe({
          next: (response) => {
            if (response.success && !!response.data) {
              let index = this.unitsList.findIndex(unit => unit.id === response.data!.unitId);
              if (index === -1) {
                this.loadUnits();
              } else {
                this.unitsList.at(index)!.projects = [...this.unitsList.at(index)!.projects ?? [], response.data];
                this.tableRows = this.unitsToTreeNodes(this.unitsList);
              }
            } else {
              console.error('Error adding project', response.error);
              this.errorMessageService.showTemporaryErrorWithTitle('Save Failed', response.error ?? '', 10000);
            }
          },
          error: (error) => {
            console.error('Error adding project', error);
            this.errorMessageService.showTemporaryErrorWithTitle('Save Failed', error.statusText ?? '', 10000);
          }
        });
      }
    })
  }

  protected readonly toTAStatusText = toTAStatusText;
}
